// Measuring and areas on the fitted grid (issue 832): the corner between
// squares nearest a point, a square's centre, the board's size in squares.
// Pure over the grid's own line positions, so a fitted grid whose squares
// are not all one size is handled the way everything else on it is.
import { axisPositions, type AreaSpan, type Cell, type Grid, type Point } from '@dragons-table/vtt-shared';

interface Extent { width: number; height: number }

const nearest = (positions: number[], v: number): number => {
  let best = 0;
  for (let k = 1; k < positions.length; k += 1) if (Math.abs(positions[k]! - v) < Math.abs(positions[best]! - v)) best = k;
  return best;
};

/** The intersection (the top-left corner of square (i, j)) nearest a board point. */
export function nearestIntersection(grid: Grid, extent: Extent, p: Point): Cell {
  return { i: nearest(axisPositions(grid, extent, 'x'), p.x), j: nearest(axisPositions(grid, extent, 'y'), p.y) };
}

/** Where an intersection is, in board pixels. */
export function intersectionAt(grid: Grid, extent: Extent, c: Cell): Point {
  const xs = axisPositions(grid, extent, 'x'); const ys = axisPositions(grid, extent, 'y');
  return { x: xs[Math.min(xs.length - 1, Math.max(0, c.i))]!, y: ys[Math.min(ys.length - 1, Math.max(0, c.j))]! };
}

/** A square's centre, in board pixels. */
export function cellCentre(grid: Grid, extent: Extent, c: Cell): Point {
  const a = intersectionAt(grid, extent, c); const b = intersectionAt(grid, extent, { i: c.i + 1, j: c.j + 1 });
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** The board's size in whole squares. */
export function boardSquares(grid: Grid, extent: Extent): { cols: number; rows: number } {
  return { cols: Math.max(0, axisPositions(grid, extent, 'x').length - 1), rows: Math.max(0, axisPositions(grid, extent, 'y').length - 1) };
}

/** Each row span of an area as one rectangle in board pixels (issue 840): a 400-ft burst is 160 rectangles, not 17,000. */
export function spanRects(spans: AreaSpan[], grid: Grid, extent: Extent): Array<{ x: number; y: number; width: number; height: number }> {
  const xs = axisPositions(grid, extent, 'x'); const ys = axisPositions(grid, extent, 'y');
  const at = (p: number[], i: number) => p[Math.max(0, Math.min(p.length - 1, i))]!;
  return spans.map((s) => { const x = at(xs, s.i0); const y = at(ys, s.j); return { x, y, width: Math.max(1, at(xs, s.i1 + 1) - x), height: Math.max(1, at(ys, s.j + 1) - y) }; });
}
